import {
  CacheStore,
  CacheConfig,
  CacheData,
  StoreEvents
} from './cache/interfaces';
import getSizeBytes from './cache/getSizeBytes';
import logger from '../../log';

const log = logger.getLogger(__filename);

interface CacheRow {
  id: string;
  key: string;
  value: CacheData;
  timestamp: number;
}
const DB_NAME = 'ksdk::agent::Cache::IndexedDB';
const STORE_NAME = 'cachedObjects';

export default class IndexedDBCache implements CacheStore<CacheData> {
  config: CacheConfig;
  events: StoreEvents;
  cachedBytes: number;
  evictHandlers: (() => boolean)[];
  newContentHandlers: (() => boolean)[];
  db: IDBDatabase | undefined;
  keySet: Set<string>;

  constructor(config: CacheConfig, events: StoreEvents) {
    this.config = config;
    this.events = events;
    this.cachedBytes = 0;
    this.evictHandlers = [];
    this.newContentHandlers = [];
    this.keySet = new Set();
  }

  initialize(): Promise<void> {
    return new Promise((res, rej) => {
      const request = indexedDB.open(DB_NAME, 1);

      request.onerror = err => {
        log.warn('Failed to create indexedDB instance', err);
        rej(err);
      };

      request.onupgradeneeded = () => {
        const db = request.result;
        // cannot create new stores outside of an upgrade.
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: 'key'
        });

        store.createIndex('id', 'id', { unique: false });
      };

      request.onsuccess = () => {
        this.db = request.result;
        const objectStore = this.db
          .transaction([STORE_NAME], 'readwrite')
          .objectStore(STORE_NAME);
        const idIndex = objectStore.index('id');
        const range = IDBKeyRange.only(this.config.id);
        idIndex.openCursor(range).onsuccess = ev => {
          const cursor = (ev.target as IDBRequest).result;
          if (cursor) {
            const row = cursor.value;
            this.keySet.add(row.key);
            cursor.continue();
          }
        };
        res();
      };
    });
  }

  makeRow(key: string, value: CacheData): CacheRow {
    return { id: this.config.id, key, value, timestamp: Date.now() };
  }

  _getInternal(key: string): Promise<CacheRow | undefined> {
    if (this.db) {
      const tx = this.db.transaction([STORE_NAME], 'readwrite');
      return new Promise(res => {
        const objectStore = tx.objectStore(STORE_NAME);
        const request = objectStore.get(key);
        request.onerror = e => {
          log.warn(`Failed request to get row for key ${key}.`, e);
          res(undefined);
        };
        request.onsuccess = () => {
          res(request.result);
        };
      });
    } else {
      return Promise.resolve(undefined);
    }
  }

  async get(key: string): Promise<CacheData | undefined> {
    const row = await this._getInternal(key);
    return row ? row.value : undefined;
  }

  set(key: string, value: CacheData): Promise<boolean> {
    if (this.db) {
      const tx = this.db.transaction([STORE_NAME], 'readwrite');
      return new Promise(res => {
        const objectStore = tx.objectStore(STORE_NAME);
        const row = this.makeRow(key, value);
        // add will throw a ConstraintError if row exists.
        const request = objectStore.add(row);
        request.onerror = () => {
          if (request.error && request.error.name == 'ConstraintError') {
            this.keySet.add(key);
            res(true);
          } else {
            log.warn(
              'Failed request to add row with error ',
              row,
              request.error || request
            );
            res(false);
          }
        };
        request.onsuccess = () => {
          this.keySet.add(key);
          this.events.onset({ key });
          res(true);
        };
      });
    } else {
      return Promise.resolve(false);
    }
  }

  delete(key: string): Promise<boolean> {
    if (this.db) {
      const tx = this.db.transaction([STORE_NAME], 'readwrite');
      return new Promise(res => {
        const objectStore = tx.objectStore(STORE_NAME);
        const request = objectStore.delete(key);
        request.onerror = e => {
          log.warn(`Failed to delete ${key} from cache`, e);
          res(false);
        };
        request.onsuccess = () => {
          this.keySet.delete(key);
          this.events.ondelete({ key });
          res(true);
        };
      });
    } else {
      return Promise.resolve(false);
    }
  }

  has(key: string): boolean {
    return this.keySet.has(key);
  }

  keys(): IterableIterator<string> {
    return this.keySet.keys();
  }

  async getPayloadSize(key: string): Promise<number> {
    const row = await this._getInternal(key);
    return row ? getSizeBytes(row.value) : 0;
  }

  async getTimestamp(key: string): Promise<number> {
    const row = await this._getInternal(key);
    return row ? row.timestamp : 0;
  }

  clear(): void {
    if (this.db) {
      const tx = this.db.transaction([STORE_NAME], 'readwrite');
      const objectStore = tx.objectStore(STORE_NAME);
      const req = objectStore.clear();
      req.onerror = e => {
        log.warn(`Failed to cleanup objectStore ${STORE_NAME}`, e);
        if (this.db) {
          this.db.close();
        }
      };
      req.onsuccess = () => {
        if (this.db) {
          this.db.close();
        }
      };
    }
    this.keySet.clear();
  }
}
